import React, { useState } from 'react';
import {
  AddEmployeesIcon,
  Box,
  Button,
  ButtonMode,
  LoadingSpinner,
  PagePanel,
  SetBenefitsIcon,
  Notification,
  NotificationType,
  NotificationSize
} from 'edenred-ui';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useTheme } from 'styled-components';

import config from '@epi-config';
import { goToPage } from '@epi-actions/navigation';
import OnboardingStepper from '@epi-components/OnboardingStepper/OnboardingStepper';
import { OnboardingSupportInformation } from '@epi-components/OnboardingSupportInformation';
import { EmptyState, Pagination } from '@epi-shared/components';
import { routerPaths } from '@epi-constants/routerPaths';
import { BeneficiaryItemsPerPage } from '@epi-constants/beneficiary';

import { useBeneficiariesList } from './hooks';
import BeneficiariesModal from './components/BeneficiariesModal/BeneficiariesModal';
import { BeneficiariesTable } from './components/BeneficiariesTable';
import { BeneficiariesTopSection } from './components/BeneficiariesTopSection';
import { BeneficiariesFilters } from './components/BeneficiariesFilters/BeneficiariesFilters';

const { enableNewBenefitGroups } = config;

const BeneficiariesList = () => {
  const { fontBold } = useTheme();
  const { t } = useTranslation();
  const reduxDispatch = useDispatch();
  const {
    isGetEmployeesLoading,
    isGroupSettingsLoading,
    isOnboardingLoading,
    groupSettings,
    employees,
    filteredEmployees,
    isLunchBenefitActive,
    selectedBeneficiaryId,
    showBeneficiaryAddedNotification,
    handleEmployeesChange,
    handleGroupChange,
    handleEmploymentTypeChange,
    showBeneficiaryModal,
    handleClose,
    setIsEmployeeAdded,
    setPage,
    page,
    paginationCount,
    sortedEmployees,
    showDetail,
    employeesOptions,
    benefitGroupOptions,
    employmentTypeOptions,
    selectedBenefitGroup,
    selectedEmploymentType,
    setshowBeneficiaryModal,
    showNotification,
    handleCloseNotification,
    preselectedGroupName,
    footer
  } = useBeneficiariesList();

  const renderPagePanel = () => {
    if (
      isGetEmployeesLoading ||
      isGroupSettingsLoading ||
      isOnboardingLoading
    ) {
      return <LoadingSpinner />;
    }

    if (!groupSettings?.length) {
      return (
        <EmptyState
          icon={<SetBenefitsIcon />}
          title={t('employees.set_benefits_title')}
          description={t('employees.set_benefits_description')}
          btnText={t('employees.set_benefits_btn')}
          btnOnClick={() => reduxDispatch(goToPage(routerPaths.manageBenefits))}
        />
      );
    }

    if (!employees?.beneficiaries.length) {
      return (
        <EmptyState
          icon={<AddEmployeesIcon />}
          title={t('employees.no_employees_title')}
          description={t('employees.no_employees_description')}
          btnText={t('employees.no_employees_btn')}
          btnOnClick={() => setshowBeneficiaryModal(true)}
        />
      );
    }

    return (
      <div>
        {showBeneficiaryAddedNotification && <BeneficiariesTopSection />}
        {enableNewBenefitGroups && (
          <BeneficiariesFilters
            employeesOptions={employeesOptions}
            selectedBenefitGroup={selectedBenefitGroup}
            benefitGroupOptions={benefitGroupOptions}
            selectedEmploymentType={selectedEmploymentType}
            employmentTypeOptions={employmentTypeOptions}
            handleEmployeesChange={handleEmployeesChange}
            handleGroupChange={handleGroupChange}
            handleEmploymentTypeChange={handleEmploymentTypeChange}
          />
        )}
        {showNotification && enableNewBenefitGroups && (
          <Box pt={3}>
            <Notification
              type={NotificationType.Info}
              size={NotificationSize.Small}
              text={
                <Trans
                  i18nKey="employees.filter.group_preselected"
                  components={{
                    b: <Box fontWeight={fontBold} display="inline-block" />
                  }}
                  values={{
                    groupName: preselectedGroupName
                  }}
                />
              }
              onClose={handleCloseNotification}
            />
          </Box>
        )}
        <BeneficiariesTable
          beneficiaries={sortedEmployees}
          allBeneficiaries={filteredEmployees}
          isLoading={isGetEmployeesLoading}
          showDetail={showDetail}
          groupSettings={groupSettings}
        />
        {filteredEmployees.length > BeneficiaryItemsPerPage && (
          <Pagination
            page={page}
            count={paginationCount}
            defaultRowsPerPage={BeneficiaryItemsPerPage}
            onChangePage={page => setPage(page)}
          />
        )}
      </div>
    );
  };

  return (
    <Box>
      <OnboardingStepper />
      <PagePanel
        title={t('containers.beneficiaries.title')}
        headerContent={
          !!groupSettings?.length && (
            <Button
              mode={ButtonMode.Secondary}
              onClick={() => {
                setshowBeneficiaryModal(true);
              }}
              id="add-employee-btn"
            >
              {t('containers.beneficiaries.add_employee.trigger')}
            </Button>
          )
        }
        footer={footer}
      >
        <Box minHeight={570}>{renderPagePanel()}</Box>
      </PagePanel>
      <OnboardingSupportInformation />

      <BeneficiariesModal
        isShowModal={showBeneficiaryModal}
        isLunchBenefitActive={isLunchBenefitActive}
        beneficiaryId={selectedBeneficiaryId}
        onClose={handleClose}
        setIsEmployeeAdded={setIsEmployeeAdded}
      />
    </Box>
  );
};

export default BeneficiariesList;
